import React, { useState, useEffect, useMemo } from 'react'
import './CompareMaterialsComponent.scss'
import { Button, Input, Spin, Empty, Pagination, Popconfirm, Tooltip } from 'antd'
import {
  CloseOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  DisconnectOutlined,
  CheckCircleFilled,
  DownOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import {
  putRelacionHomologo,
  putRelacionReemplazante,
  putRelacionModelo,
  putDesvincularHomologo,
  putDesvincularReemplazante,
  putDesvincularModelo,
  getRepuestosPorCodigoSap,
  getAllRepuestos,
} from 'src/services/axiomaService'

// En el formulario "Homólogo" se muestra como "Sustituto"
const ETIQUETAS = {
  Homólogo: 'Sustituto',
  Reemplazante: 'Reemplazante',
  'Modelo Aplicación': 'Modelo de aplicación',
  'Modelo Aplicación Principal': 'Modelo de aplicación principal',
}

const PLURALES = {
  Homólogo: 'Sustitutos',
  Reemplazante: 'Reemplazantes',
  'Modelo Aplicación': 'Modelos de aplicación',
  'Modelo Aplicación Principal': 'Modelos de aplicación principal',
}

const TAMANO_PAGINA = 20

const texto = (valor) => (valor === null || valor === undefined ? '' : String(valor).trim())

const Detalle = ({ etiqueta, valor }) =>
  texto(valor) ? (
    <div className="cmp-detalle">
      <span className="cmp-detalle__label">{etiqueta}</span>
      <span className="cmp-detalle__valor">{texto(valor)}</span>
    </div>
  ) : null

const FilaRepuesto = ({
  repuesto,
  vinculado,
  etiqueta,
  procesando,
  bloqueado,
  expandida,
  onToggle,
  onVincular,
  onDesvincular,
}) => {
  const clasificacion = [repuesto.categoria_nombre, repuesto.subcategoria_nombre].map(texto).filter(Boolean)
  const segmento = [repuesto.segmento_nombre, repuesto.subsegmento_nombre]
    .map(texto)
    .filter((v) => v && v !== 'SIN DEFINIR')
  const origen = [repuesto.repuesto_fabricante, repuesto.marca_nombre].map(texto).filter(Boolean)
  const tieneDetalle =
    texto(repuesto.revision_uso_aplicacion) ||
    texto(repuesto.revision_especificaciones_producto) ||
    texto(repuesto.revision_composicion)

  return (
    <div className={`cmp-fila${vinculado ? ' is-vinculada' : ''}${expandida ? ' is-expandida' : ''}`}>
      <div className="cmp-fila__main">
        <div className="cmp-fila__codigo">
          <span className="cmp-fila__sap">{texto(repuesto.repuesto_codigo_material_sap) || '—'}</span>
          {texto(repuesto.repuesto_npf) && <span className="cmp-fila__npf">NPF {repuesto.repuesto_npf}</span>}
        </div>

        <div className="cmp-fila__info">
          <span className="cmp-fila__desc" title={texto(repuesto.repuesto_descripcion)}>
            {texto(repuesto.repuesto_descripcion) || 'Sin descripción'}
          </span>
          {origen.length > 0 && <span className="cmp-fila__origen">{origen.join(' · ')}</span>}
        </div>

        <div className="cmp-fila__clasif">
          {clasificacion.length > 0 && <span className="cmp-fila__cat">{clasificacion.join(' › ')}</span>}
          {segmento.length > 0 && <span className="cmp-fila__seg">{segmento.join(' › ')}</span>}
        </div>

        <div className="cmp-fila__estado">
          {texto(repuesto.estado) && <span className="cmp-estado">{repuesto.estado}</span>}
        </div>

        <div className="cmp-fila__accion">
          {vinculado ? (
            <>
              <span className="cmp-fila__vinculado">
                <CheckCircleFilled /> Vinculado
              </span>
              <Popconfirm
                title={`¿Desvincular este ${etiqueta.toLowerCase()}?`}
                okText="Desvincular"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => onDesvincular(repuesto)}
                disabled={bloqueado}
              >
                <Tooltip title="Desvincular">
                  <Button
                    size="small"
                    danger
                    icon={<DisconnectOutlined />}
                    loading={procesando}
                    disabled={bloqueado}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          ) : (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<LinkOutlined />}
              loading={procesando}
              disabled={bloqueado}
              onClick={() => onVincular(repuesto)}
            >
              Vincular
            </Button>
          )}
          {tieneDetalle && (
            <button
              type="button"
              className="cmp-fila__expandir"
              onClick={onToggle}
              aria-label={expandida ? 'Ocultar detalle' : 'Ver detalle'}
            >
              <DownOutlined />
            </button>
          )}
        </div>
      </div>

      {expandida && (
        <div className="cmp-fila__detalle">
          <Detalle etiqueta="Uso / aplicación" valor={repuesto.revision_uso_aplicacion} />
          <Detalle etiqueta="Especificaciones" valor={repuesto.revision_especificaciones_producto} />
          <Detalle etiqueta="Composición" valor={repuesto.revision_composicion} />
        </div>
      )}
    </div>
  )
}

const CompareMaterialsComponent = ({
  isReadOnly = false,
  onClose,
  category,
  repuestos,
  material,
  homologos,
  reemplazantes,
  modelosAplicacionPrincipal,
  modelosAplicacion,
  onAddHomologo,
  onAddReemplazante,
  onAddModeloAplicacion,
  onAddModeloAplicacionPrincipal,
  onRemoveHomologo,
  onRemoveReemplazante,
  onRemoveModeloAplicacion,
  onRemoveModeloAplicacionPrincipal,
}) => {
  const [procesandoId, setProcesandoId] = useState(null)
  const [codigoSap, setCodigoSap] = useState('')
  const [loadingData, setLoadingData] = useState(false)
  const [repuestosLocales, setRepuestosLocales] = useState(repuestos || [])
  const [filtro, setFiltro] = useState('')
  const [pagina, setPagina] = useState(1)
  const [expandida, setExpandida] = useState(null)
  const [busquedaHecha, setBusquedaHecha] = useState(false)

  const etiqueta = ETIQUETAS[category] || category
  const etiquetaPlural = PLURALES[category] || category

  // Actualizar repuestos locales cuando cambie la prop repuestos
  useEffect(() => {
    if (repuestos) {
      setRepuestosLocales(repuestos)
    }
  }, [repuestos])

  // Cerrar con Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const fetchRepuestos = async (codigo = null) => {
    setLoadingData(true)
    let response

    if (codigo) {
      // Búsqueda por código SAP
      response = await getRepuestosPorCodigoSap(codigo)
    } else {
      // Traer todos
      response = await getAllRepuestos()
    }

    setBusquedaHecha(true)
    setPagina(1)
    setExpandida(null)
    if (response && response.repuestos) {
      setRepuestosLocales(response.repuestos)
      setLoadingData(false)
      return response.repuestos
    }
    setRepuestosLocales([])
    setLoadingData(false)
    return []
  }

  const handleConsultarCodigoSap = async () => {
    if (codigoSap.trim() === '') {
      toast.warning('Por favor ingrese un código SAP')
      return
    }
    setFiltro('')
    await fetchRepuestos(codigoSap.trim())
  }

  const handleTraerTodo = async () => {
    setCodigoSap('')
    setFiltro('')
    await fetchRepuestos()
  }

  // Relaciones ya vinculadas en la categoría actual
  const vinculados = useMemo(() => {
    if (category === 'Homólogo') return homologos || []
    if (category === 'Reemplazante') return reemplazantes || []
    if (category === 'Modelo Aplicación') return modelosAplicacion || []
    if (category === 'Modelo Aplicación Principal') return modelosAplicacionPrincipal || []
    return []
  }, [category, homologos, reemplazantes, modelosAplicacion, modelosAplicacionPrincipal])

  const codigosVinculados = useMemo(
    () => new Set(vinculados.map((v) => texto(v.codigo_material_sap))),
    [vinculados],
  )

  // El material actual no se lista: se muestra arriba en su propia tarjeta
  const resultados = useMemo(() => {
    const codigoActual = texto(material?.repuesto_codigo_material_sap)
    const base = (repuestosLocales || []).filter(
      (rep) => texto(rep.repuesto_codigo_material_sap) !== codigoActual,
    )
    const q = filtro.trim().toLowerCase()
    if (!q) return base
    return base.filter((rep) =>
      [
        rep.repuesto_codigo_material_sap,
        rep.repuesto_npf,
        rep.repuesto_descripcion,
        rep.repuesto_fabricante,
        rep.marca_nombre,
        rep.categoria_nombre,
        rep.subcategoria_nombre,
      ].some((v) => texto(v).toLowerCase().includes(q)),
    )
  }, [repuestosLocales, material, filtro])

  const paginaActual = resultados.slice((pagina - 1) * TAMANO_PAGINA, pagina * TAMANO_PAGINA)

  const handleAddCategory = async (repuesto) => {
    setProcesandoId(repuesto.repuesto_id)
    if (category === 'Homólogo') {
      const response = await putRelacionHomologo({
        revisionId: material.revision_id,
        homologoId: repuesto.repuesto_id,
        origen: 'AXIOMER',
      })
      if (response && response.success) {
        // Agregar a la lista local en lugar de recargar todo
        const nuevoHomologo = {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          npf: repuesto.repuesto_npf,
          descripcion: repuesto.repuesto_descripcion,
          fabricante: repuesto.repuesto_fabricante,
        }
        onAddHomologo?.(nuevoHomologo)
      }
    }

    if (category === 'Reemplazante') {
      const response = await putRelacionReemplazante({
        revisionId: material.revision_id,
        reemplazanteId: repuesto.repuesto_id,
        origen: 'AXIOMER',
      })
      if (response && response.success) {
        // Agregar a la lista local en lugar de recargar todo
        const nuevoReemplazante = {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          npf: repuesto.repuesto_npf,
          descripcion: repuesto.repuesto_descripcion,
          fabricante: repuesto.repuesto_fabricante,
        }
        onAddReemplazante?.(nuevoReemplazante)
      }
    }
    if (category === 'Modelo Aplicación') {
      const response = await putRelacionModelo({
        revisionId: material.revision_id,
        modeloId: repuesto.repuesto_id,
        esPrincipal: 0,
      })
      if (response && response.success) {
        // Agregar a la lista local en lugar de recargar todo
        const nuevoModelo = {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          npf: repuesto.repuesto_npf,
          descripcion: repuesto.repuesto_descripcion,
          fabricante: repuesto.repuesto_fabricante,
        }
        onAddModeloAplicacion?.(nuevoModelo)
      }
    }
    if (category === 'Modelo Aplicación Principal') {
      if (modelosAplicacionPrincipal?.length >= 10) {
        toast.error('Máximo 10 modelos de aplicación principal permitidos')
        setProcesandoId(null)
        return
      }
      const response = await putRelacionModelo({
        revisionId: material.revision_id,
        modeloId: repuesto.repuesto_id,
        esPrincipal: 1,
      })
      if (response && response.success) {
        const nuevoModelo = {
          repuesto_id: repuesto.repuesto_id,
          codigo_material_sap: repuesto.repuesto_codigo_material_sap,
          npf: repuesto.repuesto_npf,
          descripcion: repuesto.repuesto_descripcion,
          fabricante: repuesto.repuesto_fabricante,
        }
        onAddModeloAplicacionPrincipal?.(nuevoModelo)
        onAddModeloAplicacion?.(nuevoModelo)
      }
    }
    setProcesandoId(null)
  }

  const handleDeleteCategory = async (repuesto) => {
    setProcesandoId(repuesto.repuesto_id)
    if (category === 'Homólogo') {
      const response = await putDesvincularHomologo({
        revisionId: material.revision_id,
        homologoId: repuesto.repuesto_id,
      })
      if (response && response.success) {
        // Remover de la lista local en lugar de recargar todo
        onRemoveHomologo?.(repuesto.repuesto_id)
      }
    }
    if (category === 'Reemplazante') {
      const response = await putDesvincularReemplazante({
        revisionId: material.revision_id,
        reemplazanteId: repuesto.repuesto_id,
      })
      if (response && response.success) {
        // Remover de la lista local en lugar de recargar todo
        onRemoveReemplazante?.(repuesto.repuesto_id)
      }
    }
    if (category === 'Modelo Aplicación') {
      const response = await putDesvincularModelo({
        revisionId: material.revision_id,
        modeloId: repuesto.repuesto_id,
        esPrincipal: 0,
      })
      if (response && response.success) {
        // Remover de la lista local en lugar de recargar todo
        onRemoveModeloAplicacion?.(repuesto.repuesto_id)
      }
    }
    if (category === 'Modelo Aplicación Principal') {
      const response = await putDesvincularModelo({
        revisionId: material.revision_id,
        modeloId: repuesto.repuesto_id,
        esPrincipal: 1,
      })
      if (response && response.success) {
        // Remover de la lista local en lugar de recargar todo
        onRemoveModeloAplicacionPrincipal?.(repuesto.repuesto_id)
      }
    }
    setProcesandoId(null)
  }

  const ocupado = procesandoId !== null
  const totalSinFiltro = (repuestosLocales || []).filter(
    (rep) => texto(rep.repuesto_codigo_material_sap) !== texto(material?.repuesto_codigo_material_sap),
  ).length

  const renderResultados = () => {
    if (loadingData) {
      return (
        <div className="cmp-vacio">
          <Spin />
          <span>Buscando materiales…</span>
        </div>
      )
    }
    if (totalSinFiltro === 0) {
      return (
        <div className="cmp-vacio">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              busquedaHecha
                ? 'No se encontraron materiales con ese código SAP'
                : 'Busca un material por su código SAP o pulsa "Ver todos"'
            }
          />
        </div>
      )
    }
    if (resultados.length === 0) {
      return (
        <div className="cmp-vacio">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ningún resultado coincide con el filtro" />
        </div>
      )
    }
    return (
      <>
        <div className="cmp-tabla__head">
          <span>Código</span>
          <span>Descripción</span>
          <span>Clasificación</span>
          <span>Estado</span>
          <span className="cmp-tabla__head-accion">Acción</span>
        </div>
        {paginaActual.map((repuesto) => {
          const id = repuesto.repuesto_id ?? repuesto.repuesto_codigo_material_sap
          return (
            <FilaRepuesto
              key={id}
              repuesto={repuesto}
              etiqueta={etiqueta}
              vinculado={codigosVinculados.has(texto(repuesto.repuesto_codigo_material_sap))}
              procesando={procesandoId === repuesto.repuesto_id}
              bloqueado={isReadOnly || ocupado}
              expandida={expandida === id}
              onToggle={() => setExpandida((actual) => (actual === id ? null : id))}
              onVincular={handleAddCategory}
              onDesvincular={handleDeleteCategory}
            />
          )
        })}
      </>
    )
  }

  return (
    <div
      className="cmp-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
      role="presentation"
    >
      <div className="cmp-panel" role="dialog" aria-label={`Vincular ${etiqueta}`}>
        <div className="cmp-panel__head">
          <div>
            <h2 className="cmp-panel__title">Vincular {etiqueta.toLowerCase()}</h2>
            <p className="cmp-panel__subtitle">
              Busca por código SAP y vincula los materiales que funcionan como {etiqueta.toLowerCase()} de este
              material.
            </p>
          </div>
          <Button className="cmp-panel__close" icon={<CloseOutlined />} onClick={onClose} aria-label="Cerrar" />
        </div>

        {material && (
          <div className="cmp-actual">
            <span className="cmp-actual__badge">Material actual</span>
            <span className="cmp-actual__sap">{texto(material.repuesto_codigo_material_sap)}</span>
            {texto(material.repuesto_npf) && <span className="cmp-actual__npf">NPF {material.repuesto_npf}</span>}
            <span className="cmp-actual__desc">
              {texto(material.repuesto_descripcion) || texto(material.revision_descripcion_correcta)}
            </span>
          </div>
        )}

        <div className="cmp-vinculados">
          <div className="cmp-vinculados__titulo">
            {etiquetaPlural} vinculados <span className="cmp-vinculados__conteo">{vinculados.length}</span>
          </div>
          {vinculados.length === 0 ? (
            <span className="cmp-vinculados__vacio">Aún no hay ninguno. Vincúlalos desde los resultados.</span>
          ) : (
            <div className="cmp-vinculados__chips">
              {vinculados.map((v) => (
                <span key={v.repuesto_id ?? v.codigo_material_sap} className="cmp-chip" title={texto(v.descripcion)}>
                  <span className="cmp-chip__sap">{texto(v.codigo_material_sap)}</span>
                  {texto(v.descripcion) && <span className="cmp-chip__desc">{texto(v.descripcion)}</span>}
                  {!isReadOnly && (
                    <Popconfirm
                      title={`¿Desvincular este ${etiqueta.toLowerCase()}?`}
                      okText="Desvincular"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteCategory(v)}
                      disabled={ocupado}
                    >
                      <button
                        type="button"
                        className="cmp-chip__x"
                        aria-label={`Desvincular ${texto(v.codigo_material_sap)}`}
                        disabled={ocupado}
                      >
                        {procesandoId === v.repuesto_id ? <Spin size="small" /> : <CloseOutlined />}
                      </button>
                    </Popconfirm>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="cmp-toolbar">
          <Input
            className="cmp-toolbar__buscar"
            size="large"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Código SAP del material"
            value={codigoSap}
            onChange={(e) => setCodigoSap(e.target.value)}
            onPressEnter={handleConsultarCodigoSap}
            disabled={isReadOnly || loadingData}
            autoFocus
          />
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={handleConsultarCodigoSap}
            disabled={isReadOnly}
            loading={loadingData && codigoSap.trim() !== ''}
          >
            Buscar
          </Button>
          <Button
            size="large"
            icon={<UnorderedListOutlined />}
            onClick={handleTraerTodo}
            disabled={isReadOnly || loadingData}
          >
            Ver todos
          </Button>
        </div>

        {totalSinFiltro > 0 && !loadingData && (
          <div className="cmp-barra">
            <span className="cmp-barra__info">
              <b>{resultados.length}</b>{' '}
              {resultados.length === 1 ? 'material' : 'materiales'}
              {filtro.trim() && ` de ${totalSinFiltro}`}
            </span>
            <Input
              className="cmp-barra__filtro"
              size="small"
              allowClear
              prefix={<FilterOutlined />}
              placeholder="Filtrar por descripción, NPF, marca…"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value)
                setPagina(1)
              }}
            />
          </div>
        )}

        <div className="cmp-tabla">{renderResultados()}</div>

        {resultados.length > TAMANO_PAGINA && !loadingData && (
          <div className="cmp-panel__pie">
            <Pagination
              size="small"
              current={pagina}
              pageSize={TAMANO_PAGINA}
              total={resultados.length}
              onChange={(p) => {
                setPagina(p)
                setExpandida(null)
              }}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default CompareMaterialsComponent
